import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Settings } from 'lucide-react';
import { useCookieConsent } from '../contexts/CookieConsentContext';
import type { CookiePreferences } from '../contexts/CookieConsentContext';
import { isNative } from '../lib/platform';

const allAcceptedPreferences: CookiePreferences = {
  necessary: true,
  analytics: true,
  marketing: true,
};

const necessaryOnlyPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
};

export function CookieConsent() {
  const { preferences, bannerVisible, saveConsent, closeBanner } = useCookieConsent();
  const [showPreferences, setShowPreferences] = useState(false);
  const [localPrefs, setLocalPrefs] = useState<CookiePreferences>(preferences);

  // Sync local prefs when banner opens (user may have changed prefs previously)
  const handleOpenPreferences = () => {
    setLocalPrefs(preferences);
    setShowPreferences(true);
  };

  const handleAcceptAll = () => {
    setShowPreferences(false);
    saveConsent(allAcceptedPreferences);
  };

  const handleAcceptNecessary = () => {
    setShowPreferences(false);
    saveConsent(necessaryOnlyPreferences);
  };

  const handleSavePreferences = () => {
    setShowPreferences(false);
    saveConsent(localPrefs);
  };

  const handleClosePreferences = () => {
    setShowPreferences(false);
  };

  // Native apps must not show web cookie/tracking consent (Apple ATT requirement).
  if (isNative() || !bannerVisible) return null;

  return (
    <>
      {/* Backdrop for preferences modal */}
      {showPreferences && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
          onClick={handleClosePreferences}
        />
      )}

      {/* Cookie Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-lg bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl z-[9999] max-h-[80vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-white">Cookie Preferences</h3>
              <button
                onClick={handleClosePreferences}
                className="p-1 text-slate-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Necessary Cookies */}
              <div className="p-4 bg-slate-800/50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-white">Necessary Cookies</span>
                  <span className="text-xs text-teal-400 bg-teal-500/10 px-2 py-1 rounded-full">
                    Always Active
                  </span>
                </div>
                <p className="text-sm text-slate-400">
                  These cookies are essential for the website to function properly. They enable core 
                  functionality such as security, account authentication, and remembering your preferences.
                </p>
              </div>

              {/* Analytics Cookies */}
              <div className="p-4 bg-slate-800/50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-white">Analytics Cookies</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={localPrefs.analytics}
                      onChange={(e) => setLocalPrefs({ ...localPrefs, analytics: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
                  </label>
                </div>
                <p className="text-sm text-slate-400">
                  These cookies help us understand how visitors interact with our website by collecting 
                  and reporting information anonymously. This helps us improve our services.
                </p>
              </div>

              {/* Marketing Cookies */}
              <div className="p-4 bg-slate-800/50 rounded-xl">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-white">Marketing Cookies</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={localPrefs.marketing}
                      onChange={(e) => setLocalPrefs({ ...localPrefs, marketing: e.target.checked })}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-slate-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
                  </label>
                </div>
                <p className="text-sm text-slate-400">
                  These cookies are used to track visitors across websites to display relevant 
                  advertisements. They help measure the effectiveness of advertising campaigns.
                </p>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={handleSavePreferences}
                className="flex-1 px-4 py-2.5 bg-teal-500 hover:bg-teal-600 text-white font-medium rounded-xl transition-colors"
              >
                Save Preferences
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Banner */}
      {!showPreferences && (
        <div className="fixed bottom-0 inset-x-0 z-[9999] p-4 md:p-6 animate-slide-up">
          <div className="max-w-4xl mx-auto bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
            <div className="p-4 md:p-6">
              <div className="flex items-start gap-4">
                <div className="hidden sm:flex p-2.5 bg-teal-500/10 rounded-xl shrink-0">
                  <Cookie className="w-6 h-6 text-teal-400" />
                </div>
                
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    We value your privacy
                  </h3>
                  <p className="text-sm text-slate-400 mb-4">
                    We use cookies to enhance your browsing experience, serve personalized content, and 
                    analyze our traffic. By clicking "Accept All", you consent to our use of cookies. 
                    Read our{' '}
                    <Link to="/privacy" className="text-teal-400 hover:text-teal-300 underline">
                      Privacy Policy
                    </Link>
                    {' '}to learn more.
                  </p>

                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={handleAcceptAll}
                      className="px-5 py-2.5 bg-teal-500 hover:bg-teal-600 text-white font-medium rounded-xl transition-colors"
                    >
                      Accept All
                    </button>
                    <button
                      onClick={handleAcceptNecessary}
                      className="px-5 py-2.5 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-xl transition-colors"
                    >
                      Necessary Only
                    </button>
                    <button
                      onClick={handleOpenPreferences}
                      className="px-5 py-2.5 border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Customize
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.4s ease-out;
        }
      `}</style>
    </>
  );
}
